import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Comment, TaskControllerService, User} from "../../../api";
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from "@angular/forms";
import {DomSanitizer} from "@angular/platform-browser";
import {
  faArrowUpFromBracket,
  faArrowUpRightFromSquare,
  faCopy,
  faEllipsisVertical,
  faPaperPlaneTop,
  faPenToSquare,
  faReply,
  faTrash
} from "@fortawesome/pro-solid-svg-icons";
import {StateConfigService} from "../../../services/state/state-config.service";
import {PopoutContentService} from "../../../services/popout/popout.service";

import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {DatePipe} from "@angular/common";
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicTextareaComponent
} from "@buildinginformationcloud/bic-ui-components";

@Component({
  selector: 'app-task-comments',
  templateUrl: './task-comments.component.html',
  imports: [
    ReactiveFormsModule,
    FaIconComponent,
    DatePipe,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    BicDropdownElementComponent,
    BicTextareaComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./task-comments.component.scss']
})
export class TaskCommentsComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);
  private taskControllerService = inject(TaskControllerService);
  private domSanitizer = inject(DomSanitizer);
  private stateConfigService = inject(StateConfigService);
  private popoutService = inject(PopoutContentService);


  @Input() formGroup!: UntypedFormGroup;
  @Input() user!: User;
  // Gesperrt, solange die Aufgabe gespeichert wird: sonst liefe ein zweiter PUT parallel (#2736).
  @Input() actionsDisabled = false;
  @Output() saveTask: EventEmitter<any> = new EventEmitter<any>();
  @Output() reloadCommands: EventEmitter<any> = new EventEmitter<any>();

  editStates: Map<number, boolean> = new Map<number, boolean>();

  newComment!: UntypedFormGroup;

  ngOnInit(): void {
    this.newComment = this.formBuilder.group({
      date: new UntypedFormControl(),
      comment: new UntypedFormControl(),
      authorUser: new UntypedFormControl(),
      modifiedAuthorUser: new UntypedFormControl(),
    })
  }

  comment(index: number): Comment {
    return this.comments.value[index]! as Comment;
  }

  commentControl(index: number): UntypedFormGroup {
    return this.comments.controls[index] as UntypedFormGroup
  }

  get comments(): UntypedFormArray {
    return this.formGroup.get('comment') as UntypedFormArray;
  }

  get empty() {
    return this.comment(0)? Object.values(this.comment(0)).every(parameter => parameter == null) : true
  }

  editComment(index: number): boolean {
    return this.editStates.get(index) ?? false;
  }

  setEditComment(index: number, state: boolean) {
    this.editStates.set(index, state);
  }

  addComment(comment: Comment) {
    // Vor dem Einfügen ins FormArray prüfen: der Text bleibt dann im Eingabefeld stehen und geht nicht verloren.
    if (this.actionsDisabled) {
      return;
    }
    if(comment.comment){
      if (comment!.comment.trim() === "") {
        return;
      }
      const form = this.formBuilder.group({
        date: new UntypedFormControl(new Date()),
        comment: new UntypedFormControl(comment.comment),
        authorUser: new UntypedFormControl(this.user),
        modifiedAuthorUser: new UntypedFormControl(null),
      })
      if (this.empty && this.comment(0)) {
        this.comments.at(0).setValue(form.value)
      } else {
        this.comments.push(form);
      }
      this.saveTask.emit({index: this.comments.length - 1, deleted: false})
      this.newComment.reset();
    }else{
      return;
    }

  }

  addCommentFrom(comment: Comment) {
    const form = this.formBuilder.group({
      date: new UntypedFormControl(comment.date),
      comment: new UntypedFormControl(comment.comment),
      authorUser: new UntypedFormControl(comment.authorUser),
      modifiedAuthorUser: new UntypedFormControl(this.user),
    })

    this.comments.push(form);
  }

  deleteComment(commentIndex: number) {
    if (this.actionsDisabled) {
      return;
    }
    this.comments.removeAt(commentIndex)
    this.saveTask.emit({index: commentIndex, deleted: true})
  }


  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faPaperPlaneTop = faPaperPlaneTop;
  protected readonly faReply = faReply;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faTrash = faTrash;
  protected readonly faArrowUpFromBracket = faArrowUpFromBracket;
  protected readonly faCopy = faCopy;
  protected readonly faPenToSquare = faPenToSquare;
}
